// Pre-auths — in-memory mock store. No network.
import { useSyncExternalStore } from 'react';
import type { ApprovedItem, SentItem, Settings, ToSendItem } from './types';
import { buildApproved, buildMedicaidItem, buildSent, buildSettings, buildToSend } from './fixtures';
import { OWNER_NAME, PA_USER } from './utils';
import { PMS } from '@/features/shared/pms';

export const LATENCY = { send: 900, waitDocFlip: 3000 } as const;
export const AUTOPILOT_SENT_THIS_WEEK = 9;

/**
 * `autopilot` and `empty` are in the handoff; `medicaid` (Oct 22) adds a patient who can't be billed;
 * `training` (mock) leaves only one pre-auth sent through ELVA so Settings › Autopilot shows "1 of 2".
 */
type Scenario = '' | 'autopilot' | 'empty' | 'medicaid' | 'training';

/** Training before autopilot can be turned on (D300): pre-auths sent through ELVA. */
export const PREAUTH_TRAINING_TARGET = 2;
export const WAITING_DOC_META = `ELVA adds the X-ray from the Oct 27 visit, then it’s ready to send.`;

interface State {
  toSend: ToSendItem[];
  sent: SentItem[];
  approved: ApprovedItem[];
  settings: Settings;
  scenario: Scenario;
}

const listeners = new Set<() => void>();
let version = 0;
function emit(): void {
  version += 1;
  for (const l of listeners) l();
}
const openedOnce = new Set<string>();
const decidedSent = new Set<string>();
const who = `${PA_USER.name} (${PA_USER.org})`;

function readScenario(): Scenario {
  try {
    const raw = new URLSearchParams(window.location.search).get('scenario') ?? '';
    if (raw === 'autopilot' || raw === 'empty' || raw === 'medicaid' || raw === 'training') return raw;
  } catch {
    /* no window */
  }
  return '';
}

function sentRowFrom(item: ToSendItem, status: SentItem['status'], sentence: string): SentItem {
  return {
    id: item.id,
    patient: item.patient,
    payerShort: item.payer.short,
    code: item.procedures[0]?.code ?? 'D6010',
    procedure: `${item.procedureGroupId === 'crowns' ? 'crown' : 'implant'} ${item.procedures[0]?.tooth ?? ''}`.trim(),
    sentDisplay: 'Oct 15',
    appointmentDisplay: item.appointmentDisplay.split(',').slice(0, 2).join(','),
    status,
    sentence,
  };
}

function initState(): State {
  const scenario = readScenario();
  const settings = buildSettings();
  const toSend = buildToSend();
  const sent = buildSent();
  const approved = buildApproved();
  if (scenario === 'empty') {
    return { toSend: [], sent, approved, settings, scenario };
  }
  if (scenario === 'autopilot') {
    settings.autopilot = true;
    settings.autopilotSince = 'Oct 1';
    settings.autopilotBy = OWNER_NAME;
    const daniel = toSend.find((t) => t.id === 'PA-T3');
    const held = toSend.filter((t) => t.id !== 'PA-T3');
    if (daniel) sent.push(sentRowFrom(daniel, 'waiting', 'Cigna usually answers in about 14 days — by Oct 29.'));
    return { toSend: held, sent, approved, settings, scenario };
  }
  if (scenario === 'medicaid') {
    toSend.push(buildMedicaidItem());
  }
  if (scenario === 'training') {
    return { toSend, sent: sent.filter((r) => r.id === 'PA-S4'), approved: [], settings, scenario };
  }
  return { toSend, sent, approved, settings, scenario };
}

const state: State = initState();

// ---- subscription ----
export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getVersion(): number {
  return version;
}
export function usePreauthsVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

// ---- getters ----
export function getScenarioSync(): Scenario {
  return state.scenario;
}
export function getToSendSync(): ToSendItem[] {
  return state.toSend.filter((t) => !t.removed);
}
export function getSentSync(): SentItem[] {
  return state.sent;
}
export function getApprovedSync(): ApprovedItem[] {
  return state.approved;
}
export function getItemSync(id: string): ToSendItem | null {
  return state.toSend.find((t) => t.id === id) ?? null;
}
export function getSentItemSync(id: string): SentItem | null {
  return state.sent.find((s) => s.id === id) ?? null;
}
export function getSettingsSync(): Settings {
  return state.settings;
}

export function toSendCountSync(): number {
  return getToSendSync().length;
}
export function toSendTotalSync(): number {
  return getToSendSync().reduce((s, t) => s + t.treatment, 0);
}
/** Pre-auths ELVA has sent so far (the training count behind the autopilot switch). */
export function preauthsSentCountSync(): number {
  return state.sent.length;
}
/** To-send items waiting for a document someone chose to add (they don't need a person now). */
export function waitingDocCountSync(): number {
  return getToSendSync().filter((t) => t.waitingDoc === true).length;
}
export function waitingDocTotalSync(): number {
  return getToSendSync().filter((t) => t.waitingDoc === true).reduce((s, t) => s + t.treatment, 0);
}
export function sentOpenCountSync(): number {
  return state.sent.filter((s) => !s.closed && !decidedSent.has(s.id)).length;
}
export function approvedCountSync(): number {
  return state.approved.length;
}
export function isSentDecided(id: string): boolean {
  return decidedSent.has(id);
}

/**
 * "Needs you" across every tab, not just To send (Oct 18 fix): a To-send
 * decision, a Sent denial pending a decision, and an Approved item that needs
 * booking or a move before it expires all count.
 */
export function needsYouCountSync(): number {
  const toSendNeeds = getToSendSync().filter((t) => t.group === 'needs_you' && t.waitingDoc !== true).length;
  const sentNeeds = state.sent.filter((s) => s.action === 'decide' && !(s.closed ?? false) && !decidedSent.has(s.id)).length;
  const approvedNeeds = state.approved.filter(
    (a) => a.state === 'expires_first' || a.state === 'not_booked',
  ).length;
  return toSendNeeds + sentNeeds + approvedNeeds;
}

/** To-send items ELVA prepared where a person only presses Send. */
export function readyToSendCountSync(): number {
  return getToSendSync().filter((t) => t.group === 'ready').length;
}
/** Sidebar grey count (D204) = Needs you (the band's number) + Ready to send. */
export function sidebarCountSync(): number {
  return needsYouCountSync() + readyToSendCountSync();
}
export function useToSendCount(): number {
  return useSyncExternalStore(subscribe, sidebarCountSync, sidebarCountSync);
}

function pushHistory(item: ToSendItem, entry: string): void {
  item.history = [...item.history, entry];
}

export function touchOpen(id: string): void {
  const item = getItemSync(id);
  if (item && !openedOnce.has(id) && !item.removed) {
    openedOnce.add(id);
    pushHistory(item, `opened by ${who} today`);
    emit();
  }
}

// ---- to-send actions ----
export function sendItem(id: string, opts?: { status?: SentItem['status']; sentence?: string }): void {
  const item = state.toSend.find((t) => t.id === id);
  if (!item) return;
  item.removed = 'sent';
  const status = opts?.status ?? 'waiting';
  const sentence =
    opts?.sentence ?? `${item.payer.short} usually answers — ELVA will follow the answer.`;
  state.sent.unshift(sentRowFrom(item, status, sentence));
  pushHistory(item, `sent by ${who}`);
  emit();
}

/**
 * "Take an X-ray at the Oct 27 cleaning, then send". Nothing is sent: the item stays in To send with the
 * neutral `Waiting for the X-ray` badge. The mock returns it to Ready after 3 seconds (the X-ray arrived);
 * with autopilot on, ELVA then sends it. Never goes to Sent while waiting.
 */
export function waitForDoc(id: string): void {
  const item = state.toSend.find((t) => t.id === id);
  if (!item || item.removed || item.waitingDoc === true) return;
  item.waitingDoc = true;
  item.reasonLine = WAITING_DOC_META;
  pushHistory(item, `X-ray requested at the Oct 27 visit by ${who}`);
  emit();
  setTimeout(() => {
    if (item.removed || item.waitingDoc !== true) return;
    item.waitingDoc = false;
    item.group = 'ready';
    delete item.decision;
    item.reasonLine = `${item.payer.short} requires it for ${item.procedureGroup}`;
    item.documents = item.documents.map((d) =>
      d.status === 'too_old'
        ? { ...d, name: 'Periapical X-ray of #14', meta: `Oct 27, 2026 · from ${PMS} imaging`, status: 'ok' as const }
        : d,
    );
    pushHistory(item, 'the Oct 27 X-ray arrived');
    if (state.settings.autopilot) {
      sendItem(id);
    } else {
      emit();
    }
  }, LATENCY.waitDocFlip);
}

export function dontSend(id: string): void {
  const item = state.toSend.find((t) => t.id === id);
  if (!item) return;
  item.removed = 'not_sent';
  pushHistory(item, `not sent by ${who}`);
  emit();
}

export function restoreItem(id: string): void {
  const item = state.toSend.find((t) => t.id === id);
  if (!item) return;
  delete item.removed;
  state.sent = state.sent.filter((s) => !(s.id === id && s.sentDisplay === 'Oct 15'));
  emit();
}

// ---- sent / approved actions ----
export function decideSentItem(id: string): void {
  decidedSent.add(id);
  const r = state.sent.find((s) => s.id === id);
  if (r) r.closed = true;
  emit();
}
/** Review-and-resend: re-send a sent pre-auth with the attached document. No internal ids. */
export function resendSent(id: string): void {
  const r = state.sent.find((s) => s.id === id);
  if (!r) return;
  r.status = 'waiting';
  delete r.action;
  r.sentence = `Resent to ${r.payerShort} with the document attached. ELVA will follow the answer.`;
  r.timeline = [
    ...(r.timeline ?? []),
    { when: 'Oct 15', text: `Resent to ${r.payerShort} with the document attached.` },
  ];
  emit();
}
/**
 * The implant is rebooked after a cancelled visit — ELVA attaches the kept
 * approval to the new visit (D162: "Attached to the {date} visit.").
 */
export function reattachApproved(id: string, visitDisplay: string): void {
  const a = state.approved.find((x) => x.id === id);
  if (a) {
    a.state = 'booked';
    a.appointmentDisplay = visitDisplay;
    a.sentence = `Attached to the ${visitDisplay} visit.`;
    emit();
  }
}

// ---- settings actions ----
export function toggleAutopilot(on: boolean): void {
  state.settings.autopilot = on;
  if (on) {
    state.settings.autopilotSince = state.settings.autopilotSince ?? 'Oct 15';
    state.settings.autopilotBy = OWNER_NAME;
  }
  emit();
}

/** Turn a payer (or one of its procedures) on/off. Returns ids of removed unsent items. */
export function togglePayer(payerId: string, on: boolean): string[] {
  const p = state.settings.payers.find((x) => x.id === payerId);
  if (!p) return [];
  p.prepare = on;
  if (on) delete p.prepareChangedBy;
  else p.prepareChangedBy = `Turned off by ${PA_USER.name} · Oct 15`;
  emit();
  return on ? [] : removeMatching(p.short);
}
export function toggleProcedure(payerId: string, procId: string, on: boolean): string[] {
  const p = state.settings.payers.find((x) => x.id === payerId);
  const proc = p?.procedures.find((x) => x.id === procId);
  if (!p || !proc) return [];
  proc.on = on;
  proc.setBy = on ? 'Set during onboarding' : `Turned off by ${PA_USER.name} · Oct 15`;
  emit();
  return on ? [] : removeMatching(p.short);
}

function removeMatching(payerShort: string): string[] {
  const removed: string[] = [];
  for (const t of state.toSend) {
    if (!t.removed && t.payer.short === payerShort) {
      t.removed = 'not_sent';
      removed.push(t.id);
    }
  }
  if (removed.length) emit();
  return removed;
}
export function restoreMany(ids: string[]): void {
  for (const id of ids) {
    const t = state.toSend.find((x) => x.id === id);
    if (t) delete t.removed;
  }
  emit();
}
